import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

function DC4Form({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [marches, setMarches] = useState([]);
  const [soustraitants, setSoustraitants] = useState([]);
  const [isModificatif, setIsModificatif] = useState(false);
  const [dc4Numero, setDc4Numero] = useState('');
  const [formData, setFormData] = useState({
    marche_reference_interne: '',
    soustraitant_codification: '',
    type_declaration: 'acte_special',
    nature_prestations: '',
    complement: '',
    lieu_execution: '',
    delai_execution: '',
    montant_previsionnel: '',
    modalites_determination_prix: 'Prix forfaitaire',
    modalites_reglement: 'Paiement mensuel',
    paiement_direct: true,
    statut: 'Brouillon'
  });
  const [entreprises, setEntreprises] = useState([]);
  const [selectedEntreprise, setSelectedEntreprise] = useState('');
  const [saving, setSaving] = useState(false);

  const isAdmin = user && user.role === 'Admin';

  useEffect(() => {
    loadAll();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toDateInput = (val) => {
    if (!val) return '';
    return new Date(val).toISOString().split('T')[0];
  };

  const loadAll = async () => {
    try {
      const [marchesRes, stRes] = await Promise.all([
        axios.get('/api/marches'),
        axios.get('/api/soustraitants')
      ]);

      let dc4Data = null;
      if (id) {
        const response = await axios.get(`/api/dc4/${id}`);
        dc4Data = response.data;
      }

      const ordreEtat = { 'Gagné': 0, 'En cours': 1, 'Non répondu': 2, 'Perdu': 3, 'Terminé': 4, 'Declaration sans suite': 5 };
      const marchesTries = [...marchesRes.data].sort((a, b) => {
        const ea = ordreEtat[a.etat] ?? (a.retenu ? 0 : 2);
        const eb = ordreEtat[b.etat] ?? (b.retenu ? 0 : 2);
        return ea - eb;
      });
      setMarches(marchesTries);
      setSoustraitants(stRes.data.filter(st => st.statut === 'Actif'));

      // Charger les entreprises pour admin
      if (user && user.role === 'Admin') {
        try {
          const entRes = await axios.get('/api/entreprises');
          setEntreprises(entRes.data);
        } catch(e) { console.error(e); }
      }

      if (dc4Data) {
        setIsModificatif(dc4Data.type_declaration === 'acte_special_modificatif');
        setDc4Numero(dc4Data.numero_dc4 || '');
        setFormData({
          marche_reference_interne: dc4Data.marche_reference_interne ? String(dc4Data.marche_reference_interne) : '',
          soustraitant_codification: dc4Data.soustraitant_codification || '',
          type_declaration: dc4Data.type_declaration || 'acte_special',
          nature_prestations: dc4Data.nature_prestations || '',
          complement: dc4Data.complement || '',
          lieu_execution: dc4Data.lieu_execution || '',
          delai_execution: dc4Data.delai_execution || '',
          montant_previsionnel: dc4Data.montant_previsionnel || '',
          modalites_determination_prix: dc4Data.modalites_determination_prix || 'Prix forfaitaire',
          modalites_reglement: dc4Data.modalites_reglement || 'Paiement mensuel',
          paiement_direct: dc4Data.paiement_direct !== false,
          statut: dc4Data.statut || 'Brouillon',
        });
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (id) {
        await axios.put(`/api/dc4/${id}`, formData);
      } else {
        await axios.post('/api/dc4', formData);
      }
      navigate('/dc4');
    } catch (error) {
      const msg = error.response?.data?.error || 'Erreur lors de l\'enregistrement';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  // Déterminer le titre
  const getTitle = () => {
    if (id) {
      if (isModificatif) {
        return `Acte Modificatif ${dc4Numero}`;
      }
      return `Acte Spécial DC4 ${dc4Numero}`;
    }
    return 'Nouvel Acte Spécial DC4';
  };

  // Déterminer si le DC4 est en lecture seule (statut Remplacé)
  const isReadOnly = formData.statut === 'Remplacé';

  // En création : seulement les marchés "Gagné". En édition : tous les marchés.
  let displayMarches = marches;
  if (!id) {
    displayMarches = marches.filter(m => m.etat === 'Gagné' || m.retenu === true);
  }
  // Si admin, filtrer par entreprise sélectionnée
  if (!id && isAdmin && selectedEntreprise) {
    displayMarches = displayMarches.filter(m => m.entreprise_codification === selectedEntreprise);
  }

  return (
    <div className="card">
      <div className="card-header">
        <h1 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {getTitle()}
          {id && (
            <span className={'badge badge-' + (
              formData.statut === 'Validé' ? 'success' :
              formData.statut === 'Remplacé' ? 'secondary' : 'warning'
            )} style={{ fontSize: '0.7em' }}>
              {formData.statut}
            </span>
          )}
        </h1>
      </div>

      {isReadOnly && (
        <div style={{ background: '#f8f9fa', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem', color: '#666', fontStyle: 'italic' }}>
          Ce DC4 a été remplacé par un acte modificatif. Consultation uniquement.
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Section Identification */}
        <h3 style={{ fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#7f8c8d', margin: '1.25rem 0 0.75rem', paddingBottom: '0.35rem', borderBottom: '1px solid #ecf0f1' }}>Identification</h3>

        {/* Entreprise pour Admin (création uniquement) */}
        {!id && isAdmin && (
          <div className="form-group">
            <label>Entité</label>
            <select value={selectedEntreprise} onChange={e => setSelectedEntreprise(e.target.value)} className="form-control">
              <option value="">Toutes les entités</option>
              {entreprises.map(e => (
                <option key={e.codification} value={e.codification}>{e.nom} ({e.codification})</option>
              ))}
            </select>
          </div>
        )}

        <div className="form-grid">
          <div className="form-group">
            <label>Marché *</label>
            <select name="marche_reference_interne" value={String(formData.marche_reference_interne)} onChange={handleChange} className="form-control" required disabled={isReadOnly}>
              <option value="">Sélectionner un marché</option>
              {displayMarches.map(m => (
                <option key={m.reference_interne} value={String(m.reference_interne)}>
                  {m.codification ? m.codification + ' — ' : ''}{m.intitule_marche} {m.reference_client ? '(' + m.reference_client + ')' : ''}{m.etat ? ' [' + m.etat + ']' : ''}
                </option>
              ))}
            </select>
            {!id && <p style={{ fontSize: '0.75rem', color: '#999', margin: '0.25rem 0 0' }}>Seuls les marchés gagnés sont affichés pour un nouveau DC4.</p>}
          </div>

          <div className="form-group">
            <label>Sous-traitant *</label>
            <select name="soustraitant_codification" value={formData.soustraitant_codification} onChange={handleChange} className="form-control" required disabled={isReadOnly}>
              <option value="">Sélectionner un sous-traitant</option>
              {soustraitants.map(st => (
                <option key={st.codification} value={st.codification}>
                  {st.nom} - {st.ville}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Section Prestations */}
        <h3 style={{ fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#7f8c8d', margin: '1.25rem 0 0.75rem', paddingBottom: '0.35rem', borderBottom: '1px solid #ecf0f1' }}>Prestations</h3>

        <div className="form-group">
          <label>Nature des prestations *</label>
          <textarea name="nature_prestations" value={formData.nature_prestations} onChange={handleChange} className="form-control" required disabled={isReadOnly} />
        </div>

        <div className="form-group">
          <label>Complément (objet du marché)</label>
          <textarea name="complement" value={formData.complement} onChange={handleChange} className="form-control" disabled={isReadOnly} placeholder="Informations complémentaires pour la zone B du DC4..." />
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label>Lieu d'exécution</label>
            <input type="text" name="lieu_execution" value={formData.lieu_execution} onChange={handleChange} className="form-control" disabled={isReadOnly} />
          </div>

          <div className="form-group">
            <label>Délai d'exécution</label>
            <input type="text" name="delai_execution" value={formData.delai_execution} onChange={handleChange} className="form-control" disabled={isReadOnly} />
          </div>

          <div className="form-group">
            <label>Montant prévisionnel (€)</label>
            <input type="number" step="0.01" name="montant_previsionnel" value={formData.montant_previsionnel} onChange={handleChange} className="form-control" disabled={isReadOnly} />
          </div>
        </div>

        {/* Section Statut & Paiement */}
        <h3 style={{ fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#7f8c8d', margin: '1.25rem 0 0.75rem', paddingBottom: '0.35rem', borderBottom: '1px solid #ecf0f1' }}>Statut & Paiement</h3>

        <div className="form-grid">
          <div className="form-group">
            <label>Statut</label>
            <select name="statut" value={formData.statut} onChange={handleChange} className="form-control" disabled={isReadOnly}>
              <option value="Brouillon">Brouillon</option>
              <option value="Validé">Validé</option>
              {formData.statut === 'Remplacé' && <option value="Remplacé">Remplacé</option>}
            </select>
          </div>

          <div className="form-group">
            <label>
              <input type="checkbox" name="paiement_direct" checked={formData.paiement_direct} onChange={handleChange} disabled={isReadOnly} />
              {' '}Paiement direct
            </label>
          </div>
        </div>

        {!isReadOnly && (
          <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
            <button type="submit" className="btn btn-success" disabled={saving || isReadOnly}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <button type="button" onClick={() => navigate('/dc4')} className="btn btn-secondary">Annuler</button>
          </div>
        )}

        {isReadOnly && (
          <div style={{ marginTop: '2rem' }}>
            <button type="button" onClick={() => navigate('/dc4')} className="btn btn-secondary">Retour à la liste</button>
          </div>
        )}
      </form>
    </div>
  );
}

export default DC4Form;
